import { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { StepValidationContext } from '@zurich/common/contexts';
import { useTranslation } from '@zurich/common/hooks';
import { NoSSR } from '@zurich/ui/legacy/components';
import { MobileStepNavigation } from '@zurich/ui/molecules';
import { MobileOnlyNotice } from '~/components/MobileOnlyNotice';
import { vsgaStepConfig } from '~/components/stepConfig';
import { usePersistState } from '~/hooks';
import { useStepper } from '~/hooks/useStepper';
import type { VSGAStep } from '~/lib/types';
import { activeStepAtom } from '~/state';
import { isMobileDevice } from '~/utils';

export interface VorschadenGutachtenStepperProps {
  onStepChange?: (step: VSGAStep[number]) => void;
  stepName?: string;
}

export function VorschadenGutachtenStepper({
  onStepChange,
  stepName,
}: VorschadenGutachtenStepperProps) {
  const { t } = useTranslation();
  usePersistState();

  const { dataState, stepValidation, handleNext, handlePrev } = useStepper();

  const [activeStep, setActiveStep] = useRecoilState(activeStepAtom);

  const currentStep = vsgaStepConfig?.[activeStep];

  useEffect(() => {
    if (stepName) {
      setActiveStep(
        vsgaStepConfig.findIndex((step) => step.stepName === stepName)
      );
    }
  }, [setActiveStep, stepName]);

  // step change callback
  useEffect(() => {
    const step = currentStep;
    if (step) {
      onStepChange?.(step);
    }
  }, [currentStep, onStepChange]);

  const {
    title,
    nextButtonText,
    Component: Step,
    nextButtonIcon,
  } = currentStep;

  const userHasMobileDevice = isMobileDevice();
  const progress = (activeStep / vsgaStepConfig.length) * 100;

  return (
    <StepValidationContext.Provider value={stepValidation}>
      <NoSSR>
        {userHasMobileDevice ? (
          <MobileStepNavigation
            title={t(title)}
            labelNext={t(nextButtonText)}
            onClickNext={handleNext}
            onClickPrev={handlePrev}
            titleColor="darkBlue"
            nextButtonIcon={nextButtonIcon}
            loadingNext={dataState === 'loading'}
            first={vsgaStepConfig[0] === vsgaStepConfig[activeStep]}
            progress={progress}
            progressBar
          >
            <Step />
          </MobileStepNavigation>
        ) : (
          <MobileOnlyNotice />
        )}
      </NoSSR>
    </StepValidationContext.Provider>
  );
}
